/**
 * API wrapper for the fetch_images endpoint
 * Provides type-safe access to the external image search service
 */

import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';
import type { ImageSearchIds } from '@elara/domain/models/schemas/widget-schemas';

import { z } from 'zod';

import { ImageSearchIdsSchema } from '@elara/domain/models/schemas/widget-schemas';

import { getEnv } from '#env-schema.js';
import { generateImageId } from '@elara/domain/models/schemas/widget-ids';

const TripadvisorConfigSchema = z.object({ includeCategories: z.array(z.string()).optional() });

const ImageSearchRequestSchema = z.object({
  ids: ImageSearchIdsSchema,
  tags: z.array(z.string()).optional(),
  sourceFilters: z.array(z.enum(['google', 'tripadvisor', 'sabre'])).optional(),
  tripadvisor: TripadvisorConfigSchema.optional(),
  limit: z.number().int().min(1).max(200).default(6).optional(),
  preferWebp: z.boolean().optional(),
  ttlOverrideSec: z.number().int().positive().optional(),
  bypassCache: z.boolean().optional(),
  validate: z.enum(['none', 'imgur', 'all']).optional(),
});

// Response schemas
const ImageMetaSchema = z.object({ category: z.string().optional() }).passthrough();

const ImageSchema = z.object({
  id: z.string(),
  url: z.string().url(),
  width: z.number().int(),
  height: z.number().int(),
  source: z.string(),
  attribution: z.string().optional(),
  captions: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  confidence: z.number().min(0).max(1).optional(),
  meta: ImageMetaSchema.optional(),
});

const CacheInfoSchema = z.object({ hit: z.boolean(), ttlSec: z.number().int().positive() });

const ImageSearchResponseSchema = z.object({
  images: z.array(ImageSchema),
  meta: z.object({ cache: CacheInfoSchema, pruned: z.array(z.string()).optional() }),
});

// Type exports
export type ImageSearchRequest = z.infer<typeof ImageSearchRequestSchema>;
export type ImageSearchResponse = z.infer<typeof ImageSearchResponseSchema>;
export type { ImageSearchIds } from '@elara/domain/models/schemas/widget-schemas';

export type ImageResult = z.infer<typeof ImageSchema>;
export type TripadvisorConfig = z.infer<typeof TripadvisorConfigSchema>;

// API configuration. The base URL + key are read lazily from the injected env (getEnv) inside
// fetchImages — NOT at module load — so this module is safe to import under Metro/Hermes (ELARA-700).
const API_ENDPOINT = '/fetch_images';

/**
 * Whether the external image-search service is configured in this environment (both the URL and key are
 * present). Callers that use it only as an optional enhancement — e.g. the hotel-image fallback — should
 * gate on this and skip the request rather than calling {@link fetchImages} and catching the throw, so a
 * build without the key (mobile / previews) doesn't log an error per item.
 */
export function isImageSearchConfigured(): boolean {
  try {
    const env = getEnv();
    return Boolean(env.VITE_IMAGE_SEARCH_API_KEY && env.VITE_IMAGE_SEARCH_API_URL);
  } catch {
    // Env not yet injected (e.g. a test that didn't bootstrap setEnv) — treat as unconfigured and skip
    // the optional fetch rather than throwing from a reactive getter.
    return false;
  }
}

/**
 * Removes null, undefined, and empty string values from the ids object
 * @param ids - The raw ids object
 * @returns Clean ids object with only valid string values
 */
function cleanIds(ids: ImageSearchIds): Record<string, string> {
  const cleaned: Record<string, string> = {};

  if (ids.googlePlaceId && ids.googlePlaceId.length > 0) {
    cleaned.googlePlaceId = ids.googlePlaceId;
  }
  if (ids.tripadvisorLocationId && ids.tripadvisorLocationId.length > 0) {
    cleaned.tripadvisorLocationId = ids.tripadvisorLocationId;
  }
  if (ids.hotelCode && ids.hotelCode.length > 0) {
    cleaned.sabreHotelCode = ids.hotelCode;
  }
  if (ids.roomCode && ids.roomCode.length > 0) {
    cleaned.sabreRoomCode = ids.roomCode;
  }

  return cleaned;
}

/**
 * Fetches images from the external API
 * @param request - The search request parameters
 * @returns Promise resolving to the API response
 * @throws Error if the request fails or response is invalid
 */
export async function fetchImages(request: ImageSearchRequest): Promise<ImageSearchResponse> {
  const env = getEnv();
  // Validate request FIRST with raw IDs
  const validatedRequest = ImageSearchRequestSchema.parse(request);

  // Then clean the IDs for the API call (remove null/undefined)
  const cleanedIds = cleanIds(validatedRequest.ids);

  // Check for API key
  if (!env.VITE_IMAGE_SEARCH_API_KEY) {
    throw new Error(
      'Image search API key is not configured. Please set VITE_IMAGE_SEARCH_API_KEY in your environment variables.',
    );
  }

  try {
    const response = await fetch(`${env.VITE_IMAGE_SEARCH_API_URL}${API_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${env.VITE_IMAGE_SEARCH_API_KEY}` },
      body: JSON.stringify({ ...validatedRequest, ids: cleanedIds }),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data: unknown = await response.json();

    // Validate response
    const validatedResponse = ImageSearchResponseSchema.parse(data);

    // Order images by meta.ordinal if available (descending order - highest first)
    // Images without ordinal are placed at the end
    const sortedImages = validatedResponse.images.toSorted((a, b) => {
      const ordinalA = typeof a.meta?.ordinal === 'number' ? a.meta.ordinal : Number.NEGATIVE_INFINITY;
      const ordinalB = typeof b.meta?.ordinal === 'number' ? b.meta.ordinal : Number.NEGATIVE_INFINITY;
      return ordinalB - ordinalA;
    });

    return { ...validatedResponse, images: sortedImages };
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`Invalid API response: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Helper function to create a basic image search request
 * @param ids - Provider identifiers
 * @param options - Additional search options
 * @returns A properly formatted search request
 */
export function createImageSearchRequest(
  ids: ImageSearchIds,
  options: Partial<Omit<ImageSearchRequest, 'ids'>> = {},
): ImageSearchRequest {
  return { ids, ...options };
}

/**
 * Helper function to convert API images to widget format
 * @param images - Images from the API response
 * @returns Images formatted for the ImageGallery widget
 */
export function convertToWidgetImages(images: ImageResult[]): WidgetImage[] {
  return images.map((image, idx) => {
    const img = {
      ...image,
      id: image.id,
      caption: image.captions?.[0],
      attribution: image.attribution,
      alt: image.captions?.[0] || `Image from ${image.source}`,
    };
    img.id ||= generateImageId(img, idx);
    return img;
  });
}
